import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Landmark, Plus, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Where your money went — estate-level spend for one charge period.
 *
 * Unconfirmed figures are labelled. They are shown so a director can check them, never
 * as fact. Per-unit split waits until shares sum to 100%. This page does not list who
 * has paid or who has not.
 *
 * Each line says what it rests on (evidence_label): a source document attached, confirmed
 * with none attached, or unconfirmed. "Confirmed" records who checked a figure; it is not a
 * claim that a document backs it, and an attached document does not prove it correct.
 *
 * Layout order is deliberate: the line-by-line breakdown — the promise of tracing each
 * figure to a source — sits directly under the totals, before the pack-score
 * checklist. A director's first question is "where did it go", not "what's missing".
 */

const EVIDENCE_BADGE = {
    source_attached: "Confirmed · source attached",
    confirmed_no_source: "Confirmed · no source",
    source_unavailable: "Confirmed · source unavailable",
    unconfirmed: "Unconfirmed",
    director_entered: "Director-entered",
};

function pounds(pence) {
    if (pence === null || pence === undefined) return "—";
    const sign = pence < 0 ? "−" : "";
    const value = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(pence) / 100);
    return `${sign}£${value}`;
}

export default function WhereMoneyGoes() {
    const { user } = useAuth();
    const isAdmin = user?.role === "estate_admin";
    const [periods, setPeriods] = useState([]);
    const [periodId, setPeriodId] = useState(null);
    const [summary, setSummary] = useState(null);
    const [packScore, setPackScore] = useState(null);
    const [deedMatch, setDeedMatch] = useState(null);
    const [costByAgency, setCostByAgency] = useState(null);
    const [showPeriod, setShowPeriod] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        label: "2026/27", start: "2026-04-01", end: "2027-03-31",
    });
    const [lineForm, setLineForm] = useState({
        kind: "actual", category: "grounds", description: "", amount: "", source_document_id: "",
    });
    // Documents a director can attach as a line's source (their own uploads and estate-shared ones).
    const [sourceDocs, setSourceDocs] = useState([]);
    // The line whose source a director is about to share with residents, and why.
    const [shareLine, setShareLine] = useState(null);
    const [shareReason, setShareReason] = useState("");
    const [ingestBusy, setIngestBusy] = useState(false);
    // Hidden input backs the "Upload your service charge statement" shortcut so a director
    // never has to think in terms of a "charge period" first.
    const fileInputRef = useRef(null);

    const loadPeriods = useCallback(async () => {
        try {
            const res = await api.get("/finance/charge-periods");
            setPeriods(res.data);
            if (!periodId && res.data[0]) setPeriodId(res.data[0].id);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load charge periods");
        }
    }, [periodId]);

    const loadSummaryFor = useCallback(async (pid) => {
        if (!pid) {
            setSummary(null);
            setPackScore(null);
            setDeedMatch(null);
            return;
        }
        try {
            const [money, score, deed] = await Promise.all([
                api.get(`/finance/charge-periods/${pid}/where-money-goes`),
                api.get(`/finance/charge-periods/${pid}/pack-score`),
                api.get(`/finance/charge-periods/${pid}/deed-match`),
            ]);
            setSummary(money.data);
            setPackScore(score.data);
            setDeedMatch(deed.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not load this period");
        }
    }, []);

    const loadSummary = useCallback(() => loadSummaryFor(periodId), [loadSummaryFor, periodId]);

    // Estate-wide, not period-scoped: spans every charge period so a handover between
    // agents is visible even when it falls mid-period.
    const loadCostByAgency = useCallback(async () => {
        try {
            const res = await api.get("/finance/cost-by-agency");
            setCostByAgency(res.data);
        } catch (err) {
            // Agency viewers are refused this by design (see _ensure_not_agency_viewer) —
            // not a failure worth surfacing to that role.
            if (err?.response?.status !== 403) {
                toast.error(err?.response?.data?.detail || "Could not load cost by agent");
            }
        }
    }, []);

    useEffect(() => { loadPeriods(); }, [loadPeriods]);
    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { loadCostByAgency(); }, [loadCostByAgency]);

    const addPeriod = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await api.post("/finance/charge-periods", form);
            toast.success("Charge period added");
            setShowPeriod(false);
            setPeriodId(res.data.id);
            await loadPeriods();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add that period");
        } finally {
            setBusy(false);
        }
    };

    const loadSourceDocs = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const res = await api.get("/documents");
            setSourceDocs(res.data || []);
        } catch {
            setSourceDocs([]); // the picker is optional; a line can still be added without a source
        }
    }, [isAdmin]);
    useEffect(() => { loadSourceDocs(); }, [loadSourceDocs]);

    const shareSource = async () => {
        if (!shareLine) return;
        if (shareReason.trim().length < 8) {
            toast.error("Give a specific reason (at least 8 characters)");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/documents/${shareLine.source_document_id}/visibility`, {
                visibility: "estate_shared",
                reason: shareReason.trim(),
            });
            toast.success("Shared with residents");
            setShareLine(null);
            setShareReason("");
            await Promise.all([loadSummary(), loadSourceDocs()]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not share that document");
        } finally {
            setBusy(false);
        }
    };

    const addLine = async (e) => {
        e.preventDefault();
        if (!periodId) return;
        const amount_pence = Math.round(Number(lineForm.amount) * 100);
        if (Number.isNaN(amount_pence)) {
            toast.error("Enter an amount in pounds");
            return;
        }
        setBusy(true);
        try {
            const path = lineForm.kind === "budget"
                ? `/finance/charge-periods/${periodId}/budget-lines`
                : `/finance/charge-periods/${periodId}/actual-lines`;
            await api.post(path, {
                category: lineForm.category,
                description: lineForm.description,
                amount_pence,
                source_document_id: lineForm.source_document_id || null,
            });
            toast.success(lineForm.kind === "budget" ? "Budget line added" : "Spend line added and confirmed");
            setLineForm({ kind: "actual", category: "grounds", description: "", amount: "", source_document_id: "" });
            await Promise.all([loadSummary(), loadCostByAgency()]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not add that line");
        } finally {
            setBusy(false);
        }
    };

    // Read a statement into `pid`. Explicit pid (not state) so the very first upload —
    // which creates the period a moment earlier — targets the right period reliably.
    const doIngest = async (file, pid) => {
        if (!file || !pid) return;
        setIngestBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await api.post(`/finance/charge-periods/${pid}/ingest`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            if (res.data?.extraction_warning) {
                toast.warning(res.data.extraction_warning);
            } else {
                toast.success(res.data.note || "Statement read — check the figures below and confirm each one");
            }
            setPeriodId(pid);
            await Promise.all([loadPeriods(), loadSummaryFor(pid), loadCostByAgency()]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not read that statement");
        } finally {
            setIngestBusy(false);
        }
    };

    // The one-tap path: open the file picker within the click gesture, then (after the
    // file is chosen) make sure a period exists and read the statement into it. Speaks the
    // director's language (a statement) instead of asking them to model a "period" first.
    const ensurePeriod = async () => {
        if (periodId) return periodId;
        if (!isAdmin) return null;
        setBusy(true);
        try {
            const res = await api.post("/finance/charge-periods", form);
            setPeriodId(res.data.id);
            await loadPeriods();
            return res.data.id;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start a charge period");
            return null;
        } finally {
            setBusy(false);
        }
    };

    const handleStatementFile = async (file) => {
        if (!file) return;
        const pid = await ensurePeriod();
        if (pid) await doIngest(file, pid);
    };

    const openStatementPicker = () => fileInputRef.current?.click();

    const confirmLine = async (id) => {
        try {
            await api.post(`/finance/actual-lines/${id}/confirm`);
            toast.success("Marked as confirmed");
            await loadSummary();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not confirm that line");
        }
    };

    const openSourceDocument = async (line) => {
        try {
            const res = await api.get(`/documents/${line.source_document_id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = line.source_document_filename || "source-document.bin";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open the source document");
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-10" data-testid="where-money-goes-page">
            {/* Always mounted so the empty-state shortcut can open it before any period exists. */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.csv"
                className="hidden"
                data-testid="statement-ingest-input"
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    handleStatementFile(f);
                }}
            />

            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
                <Landmark size={14} /> Estate spend
            </div>
            <div className="flex items-start justify-between gap-4 mt-2 flex-wrap">
                <div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900">Where your money went</h1>
                    <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                        Line items for one charge period and how they split across the units.
                        Each line says whether a source document is attached. Unconfirmed
                        extractions stay labelled until a director checks them.
                    </p>
                </div>
                {isAdmin && (
                    <Button
                        onClick={() => setShowPeriod((s) => !s)}
                        className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 h-9 text-xs"
                        data-testid="add-period-toggle"
                    >
                        <Plus size={14} className="mr-1.5" />
                        {showPeriod ? "Cancel" : "New period"}
                    </Button>
                )}
            </div>

            {showPeriod && (
                <form onSubmit={addPeriod} className="mt-5 rounded-lg border border-slate-300 bg-white p-5 grid sm:grid-cols-3 gap-3">
                    <div>
                        <Label htmlFor="pl">Label</Label>
                        <Input id="pl" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="ps">Start</Label>
                        <Input id="ps" type="date" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
                    </div>
                    <div>
                        <Label htmlFor="pe">End</Label>
                        <Input id="pe" type="date" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
                    </div>
                    <div className="sm:col-span-3">
                        <Button type="submit" disabled={busy} className="bg-slate-900 text-white h-9 text-xs">Save period</Button>
                    </div>
                </form>
            )}

            {periods.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-1.5">
                    {periods.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setPeriodId(p.id)}
                            className={`text-xs px-2.5 py-1 rounded-full border ${
                                periodId === p.id
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white border-slate-200 text-slate-600"
                            }`}
                            data-testid={`period-${p.id}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            )}

            {!summary && periods.length === 0 && (
                <div
                    className="mt-8 rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-600"
                    data-testid="money-empty-state"
                >
                    {isAdmin ? (
                        <>
                            <p className="max-w-md mx-auto">
                                Start with the statement or budget your managing agent sent you.
                                We'll read it into line items you can check — no need to set
                                anything up first.
                            </p>
                            <Button
                                onClick={openStatementPicker}
                                disabled={busy || ingestBusy}
                                className="mt-4 bg-slate-900 hover:bg-slate-800 text-white h-10 text-sm"
                                data-testid="money-empty-upload-statement"
                            >
                                <Upload size={15} className="mr-2" /> Upload your service charge statement
                            </Button>
                            <p className="mt-3 text-xs text-slate-500">
                                PDF or text. Or{" "}
                                <button
                                    type="button"
                                    onClick={() => setShowPeriod(true)}
                                    className="underline text-slate-600 hover:text-slate-900"
                                    data-testid="money-empty-create-period"
                                >
                                    enter the year's figures by hand
                                </button>
                                .
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                A charge period is one year's service charge accounts — for example
                                2026/27. Nothing has been added yet.
                            </p>
                            <p className="mt-2 text-xs text-slate-500">
                                A director of your estate needs to add it before figures appear here.
                            </p>
                        </>
                    )}
                    <p className="mt-4 text-xs text-slate-500" data-testid="pack-score-empty">
                        The 12-point pack score needs a charge period too. Until then every item is
                        missing from the records we can see.
                    </p>
                </div>
            )}

            {summary && (
                <>
                    {/* Totals */}
                    <div className="mt-8 grid sm:grid-cols-3 gap-3">
                        <div className="border border-slate-200 bg-white rounded-lg p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Budget</div>
                            <div className="font-heading text-2xl font-bold mt-1">{pounds(summary.budget_total_pence)}</div>
                        </div>
                        <div className="border border-slate-200 bg-white rounded-lg p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Confirmed spend</div>
                            <div className="font-heading text-2xl font-bold mt-1">{pounds(summary.actual_confirmed_total_pence)}</div>
                            {summary.actual_unconfirmed_count > 0 && (
                                <div className="text-xs text-amber-800 mt-1">
                                    {summary.actual_unconfirmed_count} unconfirmed line{summary.actual_unconfirmed_count === 1 ? "" : "s"} not included
                                </div>
                            )}
                        </div>
                        <div className="border border-slate-200 bg-white rounded-lg p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Variance</div>
                            <div className="font-heading text-2xl font-bold mt-1">{pounds(summary.variance_pence)}</div>
                            {summary.actual_unconfirmed_count > 0 ? (
                                <div className="text-xs text-amber-800 mt-1" data-testid="variance-provisional-note">
                                    Confirmed spend minus budget so far — {summary.actual_unconfirmed_count} line{summary.actual_unconfirmed_count === 1 ? "" : "s"} still to confirm, so this figure is not yet complete.
                                </div>
                            ) : (
                                <div className="text-xs text-slate-500 mt-1">Confirmed spend minus budget</div>
                            )}
                        </div>
                    </div>

                    {/* Upload another statement — kept near the top so it's the obvious action. */}
                    {isAdmin && (
                        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <FileText size={16} className="text-slate-500" />
                                Read a statement or budget (PDF or text) into line items.
                            </div>
                            <Button
                                onClick={openStatementPicker}
                                disabled={ingestBusy || busy}
                                className="bg-slate-900 hover:bg-slate-800 text-white h-9 text-xs"
                                data-testid="upload-statement-button"
                            >
                                <Upload size={14} className="mr-1.5" /> {ingestBusy ? "Reading…" : "Upload statement"}
                            </Button>
                        </div>
                    )}

                    {/* The promise: line-by-line, with what each figure rests on. */}
                    {summary.actual_confirmed_count > 0 && (
                        <p className="mt-6 text-sm text-slate-700" data-testid="source-coverage">
                            {summary.actual_source_attached_count} of {summary.actual_confirmed_count} confirmed
                            figure{summary.actual_confirmed_count === 1 ? " has" : "s have"} a source document attached
                            {summary.actual_source_attached_count < summary.actual_confirmed_count
                                ? " — the others are confirmed by a director without one."
                                : "."}
                        </p>
                    )}
                    <div className="mt-3 space-y-2">
                        {summary.lines.map((line) => (
                            <div
                                key={`${line.kind}-${line.id}`}
                                className="rounded-lg border border-slate-200 bg-white p-4"
                                data-testid={`finance-line-${line.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-400">
                                            {line.kind} · {line.category}
                                        </div>
                                        <div className="font-medium text-slate-900 mt-0.5">{line.description}</div>
                                        {line.unconfirmed_reason && (
                                            <div className="text-xs text-amber-800 mt-1">{line.unconfirmed_reason}</div>
                                        )}
                                        {isAdmin && line.source_document_id && line.source_document_title
                                            && line.source_shared_with_residents === false && (
                                            <div className="text-xs text-slate-500 mt-1">
                                                Residents can't open this source yet.{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => { setShareLine(line); setShareReason(""); }}
                                                    className="underline text-slate-700 hover:text-slate-900"
                                                    data-testid={`share-source-${line.id}`}
                                                >
                                                    Share with residents
                                                </button>
                                            </div>
                                        )}
                                        {!line.unconfirmed_reason && line.evidence_status && line.evidence_status !== "source_attached" && (
                                            <div className="text-xs text-amber-800 mt-1" data-testid={`evidence-note-${line.id}`}>
                                                {line.evidence_label}
                                            </div>
                                        )}
                                        {line.source_document_id && (
                                            <div className="text-xs text-slate-500 mt-1">
                                                Source:{" "}
                                                {line.source_restricted ? (
                                                    <span data-testid={`source-doc-restricted-${line.id}`}>
                                                        attached — not shared with residents
                                                    </span>
                                                ) : line.source_document_title ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openSourceDocument(line)}
                                                        className="underline text-slate-700 hover:text-slate-900"
                                                        data-testid={`source-doc-${line.id}`}
                                                    >
                                                        {line.source_document_title}
                                                    </button>
                                                ) : (
                                                    <span data-testid={`source-doc-unavailable-${line.id}`}>
                                                        document unavailable
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className={`font-heading text-lg tabular-nums ${line.as_fact ? "text-slate-900" : "text-amber-800"}`}>
                                            {pounds(line.amount_pence)}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-wide text-slate-400">
                                            {EVIDENCE_BADGE[line.evidence_status] || (line.as_fact ? "Confirmed" : "Unconfirmed")}
                                        </div>
                                        {isAdmin && !line.as_fact && line.kind === "actual" && (
                                            <button
                                                onClick={() => confirmLine(line.id)}
                                                className="text-xs underline text-slate-600 mt-1"
                                                data-testid={`confirm-finance-line-${line.id}`}
                                            >
                                                Confirm
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Per-unit split */}
                    <section className="mt-8">
                        <h2 className="font-heading text-lg font-semibold">Per-unit split</h2>
                        <p className="text-xs text-slate-500 mt-1">{summary.apportionment.note}</p>
                        {summary.per_unit_note && (
                            <p className="text-xs text-slate-500 mt-1" data-testid="per-unit-note">{summary.per_unit_note}</p>
                        )}
                        {!summary.apportionment.sums_to_100 && (
                            <p className="text-sm text-slate-600 mt-3">
                                Recorded shares sum to {summary.apportionment.sum_percent || "0"}%, not 100%.
                                Per-unit amounts are not shown as fact until they do.
                            </p>
                        )}
                        {summary.per_unit.length > 0 && (
                            <div className="mt-3 divide-y border border-slate-200 rounded-lg bg-white">
                                {summary.per_unit.map((u) => (
                                    <div key={u.unit_id} className="flex justify-between gap-3 px-4 py-2.5 text-sm">
                                        <span>{u.unit_number || u.unit_id} · {u.share_percent}%</span>
                                        <span className="tabular-nums">{pounds(u.amount_pence)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Manual line entry — secondary to the statement upload above. */}
                    {isAdmin && (
                        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
                            <h2 className="text-sm font-medium text-slate-800">Add or correct a line by hand</h2>
                            <form onSubmit={addLine} className="mt-3 grid sm:grid-cols-5 gap-3">
                                <div>
                                    <Label htmlFor="lk">Kind</Label>
                                    <select
                                        id="lk"
                                        value={lineForm.kind}
                                        onChange={(e) => setLineForm({ ...lineForm, kind: e.target.value })}
                                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                                    >
                                        <option value="actual">Spend</option>
                                        <option value="budget">Budget</option>
                                    </select>
                                </div>
                                <div>
                                    <Label htmlFor="lc">Category</Label>
                                    <Input id="lc" required value={lineForm.category} onChange={(e) => setLineForm({ ...lineForm, category: e.target.value })} />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label htmlFor="ld">Description</Label>
                                    <Input id="ld" required value={lineForm.description} onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })} />
                                </div>
                                <div>
                                    <Label htmlFor="la">Amount (£)</Label>
                                    <Input id="la" required inputMode="decimal" value={lineForm.amount} onChange={(e) => setLineForm({ ...lineForm, amount: e.target.value })} />
                                </div>
                                <div className="sm:col-span-5">
                                    <Label htmlFor="ls">Source document (optional)</Label>
                                    <select
                                        id="ls"
                                        value={lineForm.source_document_id}
                                        onChange={(e) => setLineForm({ ...lineForm, source_document_id: e.target.value })}
                                        className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                                        data-testid="line-source-select"
                                    >
                                        <option value="">None — I am typing this from a letter or from memory</option>
                                        {sourceDocs.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.title}{d.original_filename && d.original_filename !== d.title ? ` (${d.original_filename})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Choosing a document records where the figure came from. It does not prove the figure matches it.
                                    </p>
                                </div>
                                <div className="sm:col-span-5">
                                    <Button type="submit" disabled={busy} className="bg-slate-900 text-white h-9 text-xs">
                                        Add line
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Secondary: what's missing from the agent's pack. The full 12-point
                        breakdown and draft request live on /pack-score (single source); here we
                        show only the headline and link out, to avoid duplicating it. */}
                    {packScore && (
                        <section className="mt-8 rounded-lg border border-slate-200 bg-white p-5" data-testid="pack-score">
                            <h2 className="font-heading text-lg font-semibold text-slate-900" data-testid="pack-score-headline">
                                {packScore.headline}
                            </h2>
                            <p className="text-xs text-slate-500 mt-2 max-w-2xl">{packScore.disclaimer}</p>
                            <Link
                                to="/pack-score"
                                className="inline-block mt-3 text-sm underline text-slate-700 hover:text-slate-900"
                                data-testid="pack-score-detail-link"
                            >
                                See the full 12-point breakdown and the draft information request →
                            </Link>
                        </section>
                    )}

                    {deedMatch && (
                        <details className="mt-4 rounded-lg border border-slate-200 bg-white" data-testid="deed-match">
                            <summary className="cursor-pointer px-5 py-4 font-heading text-lg font-semibold text-slate-900">
                                Spend vs recorded heads
                            </summary>
                            <div className="px-5 pb-5">
                                <p className="text-xs text-slate-500 mt-1 max-w-2xl">{deedMatch.disclaimer}</p>
                                <p className="text-sm mt-3" data-testid="deed-match-counts">
                                    Matched {deedMatch.counts.matched} · Ambiguous {deedMatch.counts.ambiguous} · Unmatched {deedMatch.counts.unmatched}
                                </p>
                                <ul className="mt-3 space-y-2">
                                    {deedMatch.rows.map((row) => (
                                        <li key={row.id} className="text-sm border border-slate-100 rounded-md px-3 py-2" data-testid={`deed-match-row-${row.id}`}>
                                            <span className="text-[11px] uppercase tracking-wide text-slate-400 mr-2">{row.status}</span>
                                            {row.description || row.category}
                                            {row.ask && <p className="text-xs text-slate-600 mt-1">Draft ask: {row.ask}</p>}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </details>
                    )}

                    {costByAgency && costByAgency.agencies.length > 0 && (
                        <section className="mt-4 rounded-lg border border-slate-200 bg-white p-5" data-testid="cost-by-agency">
                            <h2 className="font-heading text-lg font-semibold text-slate-900">
                                Cost by managing agent
                            </h2>
                            <p className="text-xs text-slate-500 mt-2 max-w-2xl">{costByAgency.note}</p>
                            <div className="mt-4 space-y-2">
                                {costByAgency.agencies.map((agency) => (
                                    <div
                                        key={agency.agency_engagement_id}
                                        className="text-sm border border-slate-100 rounded-md px-3 py-2 grid sm:grid-cols-4 gap-2"
                                        data-testid={`cost-by-agency-row-${agency.agency_engagement_id}`}
                                    >
                                        <div className="sm:col-span-2">
                                            <div className="font-medium text-slate-800">{agency.agency_name}</div>
                                            <div className="text-xs text-slate-500">
                                                {agency.appointed_at || "—"} to {agency.terminated_at || "current"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-widest text-slate-500">Budget</div>
                                            <div>{pounds(agency.budget_total_pence)}</div>
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-widest text-slate-500">Confirmed spend</div>
                                            <div>{pounds(agency.confirmed_total_pence)}</div>
                                            {agency.unconfirmed_line_count > 0 && (
                                                <div className="text-xs text-amber-800 mt-0.5">
                                                    {agency.unconfirmed_line_count} unconfirmed not included
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {(costByAgency.unattributed_line_count > 0 || costByAgency.unattributed_budget_line_count > 0) && (
                                <p className="mt-3 text-xs text-slate-500" data-testid="cost-by-agency-unattributed">
                                    {costByAgency.unattributed_line_count > 0 && (
                                        <>{costByAgency.unattributed_line_count} spend line{costByAgency.unattributed_line_count === 1 ? "" : "s"} not attributed to an agent. </>
                                    )}
                                    {costByAgency.unattributed_budget_line_count > 0 && (
                                        <>{costByAgency.unattributed_budget_line_count} budget line{costByAgency.unattributed_budget_line_count === 1 ? "" : "s"} not attributed to an agent.</>
                                    )}
                                </p>
                            )}
                        </section>
                    )}

                    <p className="mt-8 text-xs text-slate-500 max-w-2xl">{summary.disclaimer}</p>
                </>
            )}

            <Dialog open={!!shareLine} onOpenChange={(open) => { if (!open) setShareLine(null); }}>
                <DialogContent data-testid="share-source-dialog">
                    <DialogHeader>
                        <DialogTitle>Share this source with residents?</DialogTitle>
                        <DialogDescription>
                            Every resident in the estate will be able to open “{shareLine?.source_document_title}”.
                            Check first that it does not contain other residents' names, arrears or personal
                            circumstances. Anyone who opens it will have seen its contents.
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="share-reason">Why are you sharing it? (recorded in the audit log)</Label>
                        <Textarea
                            id="share-reason"
                            value={shareReason}
                            onChange={(e) => setShareReason(e.target.value)}
                            placeholder="e.g. Residents asked to see the statement behind the grounds figure"
                            data-testid="share-source-reason"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setShareLine(null)}>Cancel</Button>
                        <Button
                            type="button"
                            onClick={shareSource}
                            disabled={busy || shareReason.trim().length < 8}
                            className="bg-slate-900 text-white"
                            data-testid="share-source-confirm"
                        >
                            Share with residents
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
